/**
 * useContactSheetSurface
 *
 * Returns the four `useContact` surface callbacks
 * (`onEmailEmpty`, `onEmailInvalid`, `onConfirmSubmit`, `onResult`) wired to
 * `InfoSheet` / `ConfirmSheet` using the shared `l10n.contact.*` strings.
 *
 * Both `ContactPage` and `SupportPage` need the exact same sheet wiring
 * (Flutter inlines byte-identical English strings on both screens —
 * `contact_screen.dart:182-263` vs `support_screen.dart:181-247`), so the
 * surface is centralized here. Any future change to the sheet copy or
 * surface kind (sheet → dialog, etc.) flows through a single hook.
 *
 * Clean-Architecture note: `useContact` owns state + orchestration and
 * stays presentation-agnostic; this hook is the concrete "sheets" surface
 * choice. Consumers do:
 *
 *     const surface = useContactSheetSurface(screenKey);
 *     const { content, email, isLoading, setContent, setEmail, submit } =
 *         useContact(surface);
 */
import { useCallback, useMemo } from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import type { UseContactOptions } from './useContact';

export const useContactSheetSurface = (screenKey: string): UseContactOptions => {
    const { l10n } = useLocale();

    const onEmailEmpty = useCallback(async () => {
        await InfoSheet.show({
            title: l10n.contact.emailEmptyTitle,
            infoText: l10n.contact.emailEmptyMessage,
            okText: l10n.common.ok,
            screenKey,
        });
    }, [l10n, screenKey]);

    const onEmailInvalid = useCallback(async () => {
        await InfoSheet.show({
            title: l10n.contact.emailInvalidTitle,
            infoText: l10n.contact.emailInvalidMessage,
            okText: l10n.common.ok,
            screenKey,
        });
    }, [l10n, screenKey]);

    const onConfirmSubmit = useCallback(async (): Promise<boolean> => {
        // `ConfirmSheet.show` resolves an `ExpauseSheetResponse` on confirm,
        // or `null` on dismiss/cancel. Treat any non-null response as confirm
        // (mirrors the prior `if (!confirmed) return;` check).
        const response = await ConfirmSheet.show({
            title: l10n.contact.confirmTitle,
            confirmText: l10n.contact.confirmMessage,
            okText: l10n.contact.confirmYes,
            cancelText: l10n.contact.confirmNo,
            screenKey,
        });
        return response !== null;
    }, [l10n, screenKey]);

    const onResult = useCallback(
        async (success: boolean) => {
            await InfoSheet.show({
                title: success ? l10n.contact.successTitle : l10n.contact.errorTitle,
                infoText: success ? l10n.contact.successMessage : l10n.contact.errorMessage,
                okText: l10n.common.ok,
                screenKey,
            });
        },
        [l10n, screenKey],
    );

    return useMemo(
        () => ({ onEmailEmpty, onEmailInvalid, onConfirmSubmit, onResult }),
        [onEmailEmpty, onEmailInvalid, onConfirmSubmit, onResult],
    );
};
